/**
 * Level 15: The Gauntlet (Final Challenge)
 * 
 * Ultimate challenge combining ALL mechanics:
 * - Every mechanic from previous levels
 * - Long and demanding
 * - True test of skill
 * - Epic finale atmosphere
 * 
 * @module configs/levels/Level15_TheGauntlet
 */

/** @type {import('../LevelTypes').LevelConfig} */
const Level15_TheGauntlet = {
    id: 15,
    name: 'The Gauntlet',
    subtitle: 'Ultimate Challenge',
    description: 'The final test. Everything you\'ve learned, combined.',
    
    worldWidth: 6000,
    worldHeight: 2400,
    playerStart: { x: 150, y: 2100 },
    gravity: 1.0,
    
    theme: {
        bgGradient: ['#1a0000', '#330011'],  // Epic dark red
        platformColor: 0x8b0000,  // Dark red
        accentColor: 0xff4444,
        particleColor: 0xffaa00
    },
    
    platforms: [
        // PHASE 1: The Proving Grounds
        { x: 0, y: 2200, w: 400, h: 60 },
        { x: 500, y: 2050, w: 180, h: 25 },
        { x: 800, y: 1900, w: 200, h: 30 },
        { x: 1100, y: 2100, w: 280, h: 40 },
        
        // PHASE 2: Wind Corridor
        { x: 1450, y: 1950, w: 250, h: 35 },
        { x: 1750, y: 1750, w: 180, h: 25 },
        { x: 2050, y: 1550, w: 200, h: 30 },
        
        // PHASE 3: Lava Jump
        { x: 2350, y: 1700, w: 300, h: 45 },
        { x: 2700, y: 1500, w: 200, h: 30 },
        { x: 3000, y: 1700, w: 250, h: 35 },
        
        // PHASE 4: Gear Climb
        { x: 3300, y: 1550, w: 200, h: 30 },
        { x: 3100, y: 1350, w: 180, h: 25 },
        { x: 3400, y: 1150, w: 200, h: 30 },
        { x: 3200, y: 950, w: 180, h: 25 },
        
        // PHASE 5: Low Gravity Zone
        { x: 3500, y: 800, w: 300, h: 40 },
        { x: 3850, y: 600, w: 200, h: 25 },
        { x: 4200, y: 450, w: 250, h: 35 },
        
        // PHASE 6: Portal Maze
        { x: 4550, y: 650, w: 200, h: 30 },
        { x: 4850, y: 850, w: 250, h: 35 },
        { x: 5150, y: 650, w: 200, h: 30 },
        
        // FINAL: Victory Platform
        { x: 5500, y: 500, w: 500, h: 60 }
    ],
    
    movingPlatforms: [
        // Proving grounds
        { x: 650, y: 1980, w: 120, h: 20, moveX: 150, speed: 0.003 },
        
        // Wind corridor
        { x: 1600, y: 1850, w: 130, h: 20, moveY: 180, speed: 0.0035 },
        { x: 1900, y: 1650, w: 120, h: 20, moveX: 160, speed: 0.004 },
        
        // Lava section
        { x: 2550, y: 1600, w: 140, h: 20, moveY: 150, speed: 0.003 },
        { x: 2850, y: 1600, w: 130, h: 20, moveX: 140, speed: 0.0035 },
        
        // Gear climb
        { x: 3250, y: 1450, w: 110, h: 20, moveX: 130, speed: 0.004 },
        { x: 3300, y: 1050, w: 120, h: 20, moveY: 120, speed: 0.0035 },
        
        // Low gravity platforms
        { x: 3700, y: 700, w: 130, h: 20, moveX: 150, speed: 0.003 },
        { x: 4050, y: 520, w: 140, h: 20, moveY: 140, speed: 0.0025 },
        
        // Portal section
        { x: 4700, y: 750, w: 120, h: 20, moveX: 140, speed: 0.0035 },
        { x: 5000, y: 750, w: 130, h: 20, moveY: 160, speed: 0.003 },
        
        // Final approach
        { x: 5350, y: 580, w: 140, h: 20, moveX: 150, speed: 0.004 }
    ],
    
    spikes: [
        // Scattered throughout
        { x: 1150, y: 2100 },
        { x: 1200, y: 2100 },
        { x: 1500, y: 1950 },
        { x: 2400, y: 1700 },
        { x: 2450, y: 1700 },
        { x: 3050, y: 1700 },
        { x: 3350, y: 1550 },
        { x: 3550, y: 800 },
        { x: 4600, y: 650 },
        { x: 5550, y: 500 },
        { x: 5600, y: 500 }
    ],
    
    stars: [
        { x: 800, y: 1800 },
        { x: 2050, y: 1450 },
        { x: 3000, y: 1600 },
        { x: 3400, y: 850 },
        { x: 4200, y: 350 },
        { x: 5150, y: 550 }  // 6 stars for the final level!
    ],
    
    enemies: [
        { x: 1200, y: 2050 },
        { x: 2450, y: 1650 },
        { x: 3150, y: 1500 },
        { x: 3650, y: 750 },
        { x: 4950, y: 800 },
        { x: 5700, y: 450 }
    ],
    
    door: { x: 5850, y: 350, w: 60, h: 300 },
    button: { x: 5700, y: 495 },
    box: { x: 5550, y: 400, floating: true },  // Final magnetic challenge
    goal: { x: 5920, y: 400 },

    decorations: [
        { key: 'decor-aurora', centerInLevel: true, scale: 12, alpha: 0.25, scrollFactor: 0.1, depth: -55 },
        { key: 'decor-gear', centerInLevel: true, scale: 8, alpha: 0.2, scrollFactor: 0.18, depth: -50 }
    ],
    
    // Lava pits
    lavaZones: [
        { x: 2400, y: 2000, w: 550, h: 400 }  // Lava under the jump section
    ],
    
    // Wind tunnels
    windZones: [
        { x: 1400, y: 1600, w: 400, h: 500, forceX: 2.5, forceY: -0.5 },
        { x: 3400, y: 400, w: 500, h: 600, forceX: 0, forceY: -2 }  // Upward wind
    ],
    
    // Strategic portals
    portals: [
        { x1: 4500, y1: 600, x2: 5100, y2: 600, color: 0xff4444 }
    ],
    
    // Help along the way
    trampolines: [
        { x: 1050, y: 2100, w: 80 },
        { x: 2300, y: 1700, w: 80 },
        { x: 3050, y: 950, w: 80 },
        { x: 4500, y: 650, w: 80 }
    ],
    
    speedrunTargets: { gold: 90, silver: 120, bronze: 160 }
};

export default Level15_TheGauntlet;
